import { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, Plus } from 'lucide-react';

const InfiniteMarbleRun = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const marblesRef = useRef([]);
  const animationRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const scrollOffsetRef = useRef(0);
  const segmentsRef = useRef([]);
  const nextSegmentYRef = useRef(0);

  // Generate a segment of the marble run (ramps, spirals, funnels)
  const generateSegment = useCallback((startY) => {
    const canvas = canvasRef.current;
    const width = canvas.width;
    const segmentHeight = 400;
    const elements = [];
    
    // Random segment type
    const segmentType = Math.floor(Math.random() * 5);
    
    if (segmentType === 0) {
      // Zigzag ramps
      for (let i = 0; i < 4; i++) {
        const isLeft = i % 2 === 0;
        elements.push({
          type: 'ramp',
          x: isLeft ? 100 : width - 250,
          y: startY + i * 100,
          width: 200,
          angle: isLeft ? 15 : -15,
          color: `hsl(${260 + i * 20}, 70%, 50%)`
        });
      }
    } else if (segmentType === 1) {
      // Spiral funnel
      const centerX = width / 2;
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const radius = 80 + i * 15;
        elements.push({
          type: 'platform',
          x: centerX + Math.cos(angle) * radius - 40,
          y: startY + i * 45,
          width: 80,
          height: 8,
          color: `hsl(${180 + i * 15}, 70%, 50%)`
        });
      }
    } else if (segmentType === 2) {
      // Cascading steps
      for (let i = 0; i < 6; i++) {
        elements.push({
          type: 'platform',
          x: 100 + (i % 3) * 150,
          y: startY + i * 65,
          width: 120,
          height: 12,
          color: `hsl(${320 + i * 10}, 70%, 50%)`
        });
      }
    } else if (segmentType === 3) {
      // Bouncy trampolines
      for (let i = 0; i < 3; i++) {
        elements.push({
          type: 'bouncer',
          x: 150 + i * 200,
          y: startY + 100 + i * 100,
          width: 100,
          height: 15,
          color: '#FF6B6B',
          bouncePower: 15
        });
      }
    } else {
      // Pinball bumpers
      for (let i = 0; i < 5; i++) {
        elements.push({
          type: 'bumper',
          x: 100 + Math.random() * (width - 200),
          y: startY + 50 + i * 70,
          radius: 20 + Math.random() * 15,
          color: `hsl(${40 + Math.random() * 40}, 90%, 55%)`
        });
      }
    }
    
    return {
      startY,
      height: segmentHeight,
      elements
    };
  }, []);

  const initSegments = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    segmentsRef.current = [];
    nextSegmentYRef.current = 0;
    
    // Generate initial segments
    for (let i = 0; i < 5; i++) {
      const segment = generateSegment(nextSegmentYRef.current);
      segmentsRef.current.push(segment);
      nextSegmentYRef.current += segment.height;
    }
  }, [generateSegment]);

  const spawnMarble = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    marblesRef.current.push({
      id: Date.now() + Math.random(),
      x: canvas.width / 2 + (Math.random() - 0.5) * 100,
      y: scrollOffsetRef.current - 20,
      vx: (Math.random() - 0.5) * 3,
      vy: 2,
      radius: 10 + Math.random() * 6,
      hue: Math.random() * 360,
      trail: []
    });
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = Math.min(window.innerWidth, 800);
    canvas.height = window.innerHeight;

    initSegments();

    // Spawn initial marbles
    const spawnTimers = [];
    for (let i = 0; i < 5; i++) {
      spawnTimers.push(setTimeout(() => spawnMarble(), i * 300));
    }

    // Keep the canvas pixel buffer in sync with the viewport on resize/rotation
    const handleResize = () => {
      const c = canvasRef.current;
      if (!c) return;
      c.width = Math.min(window.innerWidth, 800);
      c.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      spawnTimers.forEach(clearTimeout);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [initSegments, spawnMarble]);

  useEffect(() => {
    if (isPaused) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }

    const animate = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;
      
      // Scroll down continuously
      scrollOffsetRef.current += 1.5;
      
      // Generate new segments as needed
      while (nextSegmentYRef.current < scrollOffsetRef.current + height + 400) {
        const segment = generateSegment(nextSegmentYRef.current);
        segmentsRef.current.push(segment);
        nextSegmentYRef.current += segment.height;
      }
      
      // Remove old segments
      segmentsRef.current = segmentsRef.current.filter(
        seg => seg.startY + seg.height > scrollOffsetRef.current - 100
      );
      
      // Dark gradient background
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(1, '#16213e');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Draw wall guides
      ctx.fillStyle = 'rgba(106, 90, 205, 0.3)';
      ctx.fillRect(0, 0, 50, height);
      ctx.fillRect(width - 50, 0, 50, height);

      // Draw segments
      segmentsRef.current.forEach(segment => {
        segment.elements.forEach(el => {
          const screenY = el.y - scrollOffsetRef.current;
          
          if (screenY < -50 || screenY > height + 50) return;
          
          ctx.shadowBlur = 15;
          ctx.shadowColor = el.color;
          
          if (el.type === 'ramp') {
            ctx.save();
            ctx.translate(el.x + el.width / 2, screenY);
            ctx.rotate((el.angle * Math.PI) / 180);
            ctx.fillStyle = el.color;
            ctx.fillRect(-el.width / 2, -6, el.width, 12);
            ctx.restore();
          } else if (el.type === 'platform') {
            ctx.fillStyle = el.color;
            ctx.beginPath();
            if (typeof ctx.roundRect === 'function') {
              ctx.roundRect(el.x, screenY, el.width, el.height, 4);
            } else {
              ctx.rect(el.x, screenY, el.width, el.height);
            }
            ctx.fill();
          } else if (el.type === 'bouncer') {
            ctx.fillStyle = el.color;
            ctx.beginPath();
            ctx.ellipse(el.x + el.width / 2, screenY + el.height / 2, el.width / 2, el.height / 2, 0, 0, Math.PI * 2);
            ctx.fill();
          } else if (el.type === 'bumper') {
            ctx.fillStyle = el.color;
            ctx.beginPath();
            ctx.arc(el.x, screenY, el.radius, 0, Math.PI * 2);
            ctx.fill();
          }
        });
      });

      ctx.shadowBlur = 0;

      // Update and draw marbles
      marblesRef.current = marblesRef.current.filter(marble => {
        // Physics
        marble.vy += 0.4; // gravity
        marble.vx *= 0.99; // friction
        marble.x += marble.vx;
        marble.y += marble.vy;
        
        // Wall collisions
        if (marble.x < 50 + marble.radius) {
          marble.x = 50 + marble.radius;
          marble.vx = Math.abs(marble.vx) * 0.8;
        }
        if (marble.x > width - 50 - marble.radius) {
          marble.x = width - 50 - marble.radius;
          marble.vx = -Math.abs(marble.vx) * 0.8;
        }
        
        // Check collisions with elements
        segmentsRef.current.forEach(segment => {
          segment.elements.forEach(el => {
            if (el.type === 'platform') {
              const elTop = el.y;
              const elBottom = el.y + (el.height || 12);

              if (marble.y + marble.radius > elTop &&
                  marble.y - marble.radius < elBottom &&
                  marble.x > el.x - marble.radius &&
                  marble.x < el.x + el.width + marble.radius) {

                if (marble.vy > 0) {
                  marble.y = elTop - marble.radius;
                  marble.vy = -Math.abs(marble.vy) * 0.6;
                }
              }
            } else if (el.type === 'ramp') {
              // Ramps are drawn rotated about (el.x + el.width/2, el.y) as a
              // 12px-tall plank (see render above). Transform the marble into
              // the ramp's local (unrotated) space so the hitbox matches the
              // rotated visual instead of an axis-aligned band.
              const cx = el.x + el.width / 2;
              const cy = el.y;
              const rad = (el.angle * Math.PI) / 180;
              const cos = Math.cos(rad);
              const sin = Math.sin(rad);
              const relX = marble.x - cx;
              const relY = marble.y - cy;
              const localX = relX * cos + relY * sin;
              const localY = -relX * sin + relY * cos;

              const halfWidth = el.width / 2;
              const halfHeight = 6;

              if (localX > -halfWidth - marble.radius &&
                  localX < halfWidth + marble.radius &&
                  localY + marble.radius > -halfHeight &&
                  localY - marble.radius < halfHeight &&
                  localY < 0) {

                const vLocalX = marble.vx * cos + marble.vy * sin;
                const vLocalY = -marble.vx * sin + marble.vy * cos;

                if (vLocalY > 0) {
                  const newLocalY = -halfHeight - marble.radius;
                  marble.x = cx + localX * cos - newLocalY * sin;
                  marble.y = cy + localX * sin + newLocalY * cos;

                  const bouncedLocalY = -vLocalY * 0.6;
                  marble.vx = vLocalX * cos - bouncedLocalY * sin;
                  marble.vy = vLocalX * sin + bouncedLocalY * cos;

                  // Add some horizontal movement down the slope
                  marble.vx += el.angle > 0 ? 2 : -2;
                }
              }
            } else if (el.type === 'bouncer') {
              const centerX = el.x + el.width / 2;
              const centerY = el.y + el.height / 2;
              const dx = marble.x - centerX;
              const dy = marble.y - centerY;
              const radiusX = el.width / 2 + marble.radius;
              const radiusY = el.height / 2 + marble.radius;
              const normalizedDist = (dx * dx) / (radiusX * radiusX) + (dy * dy) / (radiusY * radiusY);

              if (normalizedDist < 1) {
                marble.vy = -el.bouncePower;
                marble.vx += (Math.random() - 0.5) * 5;
              }
            } else if (el.type === 'bumper') {
              const dx = marble.x - el.x;
              const dy = marble.y - el.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              
              if (dist < marble.radius + el.radius) {
                const angle = Math.atan2(dy, dx);
                const force = 8;
                marble.vx = Math.cos(angle) * force;
                marble.vy = Math.sin(angle) * force;
              }
            }
          });
        });
        
        // Store trail
        marble.trail.push({ x: marble.x, y: marble.y - scrollOffsetRef.current });
        if (marble.trail.length > 20) marble.trail.shift();
        
        // Draw trail
        marble.trail.forEach((point, i) => {
          const alpha = i / marble.trail.length;
          ctx.beginPath();
          ctx.arc(point.x, point.y, marble.radius * alpha * 0.5, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${marble.hue}, 70%, 60%, ${alpha * 0.3})`;
          ctx.fill();
        });
        
        // Draw marble
        const screenY = marble.y - scrollOffsetRef.current;
        
        ctx.beginPath();
        ctx.arc(marble.x, screenY, marble.radius, 0, Math.PI * 2);
        
        const marbleGradient = ctx.createRadialGradient(
          marble.x - marble.radius * 0.3, 
          screenY - marble.radius * 0.3, 
          0,
          marble.x, 
          screenY, 
          marble.radius
        );
        marbleGradient.addColorStop(0, `hsl(${marble.hue}, 80%, 70%)`);
        marbleGradient.addColorStop(1, `hsl(${marble.hue}, 70%, 40%)`);
        
        ctx.fillStyle = marbleGradient;
        ctx.shadowBlur = 20;
        ctx.shadowColor = `hsl(${marble.hue}, 70%, 60%)`;
        ctx.fill();
        
        // Keep marble if it's on screen
        return marble.y - scrollOffsetRef.current < height + 100;
      });

      ctx.shadowBlur = 0;

      // Spawn new marbles
      if (Math.random() < 0.02 && marblesRef.current.length < 12) {
        spawnMarble();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPaused, generateSegment, spawnMarble]);

  return (
    <div className="min-h-screen bg-[#1a1a2e] relative overflow-hidden flex justify-center">
      <canvas ref={canvasRef} className="absolute" />

      <div className="relative z-10 w-full max-w-4xl p-4 md:p-6">
        <header className="flex justify-between items-center">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="glass-effect text-white px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold hover:bg-white/20 transition-all flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </button>
          
          <h1 className="text-2xl md:text-4xl font-bold text-white">Infinite Marble Run</h1>
          
          <div className="flex gap-2">
            <button
              data-testid="add-marble"
              onClick={spawnMarble}
              className="glass-effect text-white px-4 py-2 md:py-3 rounded-full font-semibold hover:bg-white/20 transition-all flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
            </button>
            <button
              data-testid="toggle-pause"
              onClick={() => setIsPaused(!isPaused)}
              className="glass-effect text-white px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold hover:bg-white/20 transition-all flex items-center gap-2"
            >
              {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              <span className="hidden sm:inline">{isPaused ? 'Resume' : 'Pause'}</span>
            </button>
          </div>
        </header>
        
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 glass-effect px-6 py-3 rounded-2xl text-white text-center">
          <p>Watch the marbles roll down ramps, spirals & bumpers! Tap + to add more marbles.</p>
        </div>
      </div>
    </div>
  );
};

export default InfiniteMarbleRun;
